import { RefreshChunky } from '@eventbrite/eds-iconography';
import { InputField } from '@eventbrite/eds-input-field';
import { TextList } from '@eventbrite/eds-text-list';
import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useDropdown } from '../../../containers/hooks/dropdownHooks';
import { getDomainSuggestions } from '../../../utils';
import './EmailField.scss';

const EmailField = ({
    field,
    form: { errors, touched },

    required,
    buttonClasses,
    isStatic,
    onBlur,
    onChange,
    onKeyUp,
    onFormReset,
    autoFocus,
    bottomSpacing,
    suggestDomains,
    setFieldValue,
    shortLabel,
}: any) => {
    let resetButton = null;
    // User are unable to login when the page is loading, due to "dynamic" style of the input.
    // When a loading state for LookupForm component were implemented, we should remove this prop.
    const style = isStatic ? 'static' : 'dynamic';
    const [domainSuggestions, setDomainSuggestions] = React.useState<object[]>(
        [],
    );
    const [domainSuggestionsIsHidden, setDomainSuggestionsIsHidden] =
        React.useState<boolean>(true);

    // Needed to hide dropdown when clicking outside
    const ref = useDropdown(null, setDomainSuggestionsIsHidden);

    if (onFormReset) {
        const IconButton = loadable(
            () => import('@eventbrite/eds-icon-button'),
            {
                resolveComponent: (component: any) => component.IconButton,
            },
        );

        resetButton = (
            <IconButton
                iconType={<RefreshChunky />}
                onClick={onFormReset}
                title={gettext('Reset Form')}
            />
        );
    }

    const shouldShowErrors = touched.email && !!errors.email;
    const inputFieldClasses = classNames(
        buttonClasses,
        'auth-lib__email-input-field',
    );
    const domainSuggestionsClasses = classNames(
        'auth-lib__email-input-field--domain-suggestions',
        {
            'display-hidden': domainSuggestionsIsHidden,
            'display-alongside-error': shouldShowErrors,
        },
    );

    const handleSuggestDomainOnKeyUp = (inputValue: string) => {
        const domainSuggestions = getDomainSuggestions(inputValue);
        setDomainSuggestions(domainSuggestions);

        domainSuggestions.length
            ? setDomainSuggestionsIsHidden(false)
            : setDomainSuggestionsIsHidden(true);
    };

    return (
        <div className={inputFieldClasses} ref={ref}>
            <InputField
                id="email"
                name="email"
                label={shortLabel ? gettext('Email') : gettext('Email address')}
                type="email"
                role="textbox"
                style={style}
                onBlur={(value: any) => {
                    onBlur?.(value);
                }}
                onChange={onChange}
                onKeyUp={(value: any) => {
                    onKeyUp?.(value);
                    suggestDomains &&
                        handleSuggestDomainOnKeyUp?.(value?.value);
                }}
                data-testid="auth-lib-email-field"
                suffix={resetButton}
                autoFocus={autoFocus}
                field={field}
                required={required}
                hasError={shouldShowErrors}
                annotationNote={shouldShowErrors && errors?.email}
                bottomSpacing={bottomSpacing}
                autoComplete={suggestDomains && setFieldValue ? 'off' : 'email'}
            />
            {suggestDomains && setFieldValue && (
                <div className={domainSuggestionsClasses}>
                    <TextList
                        items={domainSuggestions}
                        onItemSelect={(data: string) => {
                            setFieldValue('email', data);
                            setDomainSuggestionsIsHidden(true);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

EmailField.propTypes = {
    buttonClasses: PropTypes.string,
    required: PropTypes.bool,
    submitFailed: PropTypes.bool,
    isStatic: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFormReset: PropTypes.func,
};

export default EmailField;
